import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Tooltip,
  Chip,
  Checkbox,
  Autocomplete,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import { ApiClient } from '../services/api';
import { env } from '../config/env';
import type {
  Transcript,
  TranscriptCourse,
  TranscriptQueryParams,
  StudentTranscriptRow,
} from '../services/api';
import {
  downloadCSV,
  fetchAllTranscriptStudents,
  fetchDownloadStatusForIds,
  transcriptStudentsToCSV,
} from '../utils/csvExport';
import { useAuth } from '../contexts/AuthContext';
import { usePartner } from '../contexts/PartnerContext';
import { usePartnerScope } from '../contexts/PartnerScopeContext';
import { SelectPartnerPrompt } from '../components/SelectPartnerPrompt';
import { useTranscriptDownloadStatus } from '../hooks/useTranscriptDownloadStatus';
import {
  type ReviewStatus,
  ALL_REVIEW_STATUSES,
  getReviewStatus,
  isCourseNew,
  isPreLaunchReviewed,
  preLaunchReviewedDescription,
  reviewStatusConfig,
} from '../utils/transcriptReviewStatus';

type SortColumn = 'latest_certification_date' | 'student_name' | 'course_count';
type SortOrder = 'asc' | 'desc';
const DEFAULT_REVIEW_FILTER: ReviewStatus[] = [];

function ReviewStatusChip({
  status,
  preLaunch = false,
}: {
  status: ReviewStatus;
  preLaunch?: boolean;
}): React.ReactElement {
  const c = reviewStatusConfig[status];
  return (
    <Tooltip
      arrow
      enterDelay={150}
      title={
        <Box sx={{ py: 0.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
            {c.label}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
            {preLaunch ? preLaunchReviewedDescription : c.description}
          </Typography>
        </Box>
      }
    >
      <Chip
        label={c.label}
        size="small"
        tabIndex={0}
        sx={{
          bgcolor: c.bg,
          color: c.color,
          fontWeight: 600,
          fontSize: '0.75rem',
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
}

export function Transcripts(): React.ReactElement {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { idToken, user } = useAuth();
  const { activePartnerId: partnerId, isInternal } = usePartnerScope();
  const { institutionName, portalLaunchDate } = usePartner();
  const PARTNER_ID = partnerId ?? 0;
  const PARTNER_ID_STR = String(PARTNER_ID);
  const api = useMemo(() => new ApiClient(idToken, env.transcriptApiUrl), [idToken]);
  const backendApi = useMemo(() => new ApiClient(idToken, env.apiUrl), [idToken]);

  const [students, setStudents] = useState<StudentTranscriptRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Query state
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState<SortColumn>('latest_certification_date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Date filters
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);

  // Course filter
  const [availableCourses, setAvailableCourses] = useState<TranscriptCourse[]>([]);
  const [selectedCourses, setSelectedCourses] = useState<TranscriptCourse[]>([]);

  // Review status filter
  const [reviewFilter, setReviewFilter] = useState<ReviewStatus[]>([...DEFAULT_REVIEW_FILTER]);

  // Checkbox selection (tracks user_id values)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Expanded student row
  const [expandedStudentId, setExpandedStudentId] = useState<number | null>(null);
  const [expandedCourses, setExpandedCourses] = useState<Map<number, Transcript[]>>(new Map());
  const [expandedLoading, setExpandedLoading] = useState<Set<number>>(new Set());

  // CSV export
  const [exporting, setExporting] = useState(false);

  // Bulk download
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // A proper subset of statuses (not empty, not all three) turns on the
  // server-side review filter; the transcript API computes review status from
  // download records + the launch date and returns one filtered, paginated
  // page (SOPHIA-35146 — replaces the old client-side full-fetch that burst the
  // rate limiter for large partners).
  const reviewFilterActive =
    reviewFilter.length > 0 && reviewFilter.length < ALL_REVIEW_STATUSES.length;

  // Per-page download status drives the "downloaded by you / your org" PDF
  // checkmark and, in unfiltered mode, the status chip. Runs in every mode now
  // that the page is always a single server request.
  const pageTranscriptIds = useMemo(() => students.flatMap((s) => s.transcript_ids), [students]);
  const { myDownloads, orgDownloads, markDownloaded } = useTranscriptDownloadStatus(
    pageTranscriptIds,
    backendApi,
    PARTNER_ID_STR
  );

  // Chip status: prefer the server's authoritative review_status when filtering
  // (so the chip agrees with why the row is in the result), else derive it from
  // the page's download records.
  const statusOfRow = useCallback(
    (student: StudentTranscriptRow): ReviewStatus =>
      student.review_status ??
      getReviewStatus(student, myDownloads, orgDownloads, portalLaunchDate),
    [myDownloads, orgDownloads, portalLaunchDate]
  );

  const dateFromStr = dateFrom ? dateFrom.format('YYYY-MM-DD') : '';
  const dateToStr = dateTo ? dateTo.format('YYYY-MM-DD') : '';
  const courseIdsStr =
    selectedCourses.length > 0 ? selectedCourses.flatMap((c) => c.ids ?? [c.id]).join(',') : '';

  // The server returns exactly the page to render (filtered + paginated), so
  // the table shows `students` directly and the count is the server `total`.
  const displayTotal = total;
  const filteredStudents = students;

  // Fetch courses on mount
  useEffect(() => {
    if (partnerId == null) return;
    api
      .getTranscriptCourses(PARTNER_ID)
      .then((res) => {
        setAvailableCourses(res.items);
      })
      .catch((err) => {
        console.error('[Transcripts] Failed to fetch courses:', err);
      });
  }, [api, partnerId, PARTNER_ID]);

  const buildParams = useCallback((): TranscriptQueryParams => {
    const params: TranscriptQueryParams = {
      page: page + 1,
      per_page: rowsPerPage,
      sort: sortColumn,
      order: sortOrder,
    };
    if (search) params.search = search;
    if (dateFrom) params.date_from = dateFrom.format('YYYY-MM-DD');
    if (dateTo) params.date_to = dateTo.format('YYYY-MM-DD');
    if (selectedCourses.length > 0) {
      params.course_ids = selectedCourses.flatMap((c) => c.ids ?? [c.id]).join(',');
    }
    // Review status filtered server-side (SOPHIA-35146). Only sent for a proper
    // subset; the launch date lets the server apply the pre-launch rule.
    if (reviewFilterActive) {
      params.review_status = reviewFilter.join(',');
      if (portalLaunchDate) params.portal_launch_date = portalLaunchDate;
    }
    return params;
  }, [
    page,
    rowsPerPage,
    sortColumn,
    sortOrder,
    search,
    dateFrom,
    dateTo,
    selectedCourses,
    reviewFilterActive,
    reviewFilter,
    portalLaunchDate,
  ]);

  const fetchStudents = useCallback(async () => {
    if (partnerId == null) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.getTranscriptsByStudent(PARTNER_ID, buildParams());
      setStudents(data.items);
      setTotal(data.total);
    } catch (err) {
      console.error('[Transcripts] Failed to fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transcripts');
      // Never leave the previous (possibly differently-filtered) rows on screen
      // under an error — show an empty result with the banner instead.
      setStudents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api, buildParams, partnerId, PARTNER_ID]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleSearch = () => {
    setSearch(searchInput.trim());
    setPage(0);
    setSelectedIds(new Set());
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(0);
    setSelectedIds(new Set());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const handleClearDateFrom = () => {
    setDateFrom(null);
    setPage(0);
    setSelectedIds(new Set());
  };
  const handleClearDateTo = () => {
    setDateTo(null);
    setPage(0);
    setSelectedIds(new Set());
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    setSearch('');
    setDateFrom(null);
    setDateTo(null);
    setSelectedCourses([]);
    setReviewFilter([...DEFAULT_REVIEW_FILTER]);
    setPage(0);
    setSelectedIds(new Set());
  };

  const hasActiveFilters =
    !!search ||
    !!dateFrom ||
    !!dateTo ||
    selectedCourses.length > 0 ||
    reviewFilter.length !== DEFAULT_REVIEW_FILTER.length ||
    !DEFAULT_REVIEW_FILTER.every((s) => reviewFilter.includes(s));

  // Expand/collapse a student row — lazy load courses on first expand
  const handleToggleExpand = async (userId: number) => {
    if (expandedStudentId === userId) {
      setExpandedStudentId(null);
      return;
    }
    setExpandedStudentId(userId);
    if (!expandedCourses.has(userId)) {
      setExpandedLoading((prev) => new Set(prev).add(userId));
      try {
        const data = await api.getTranscripts(PARTNER_ID, {
          student_id: String(userId),
          per_page: 100,
          sort: 'certification_date',
          order: 'desc',
        });
        setExpandedCourses((prev) => new Map(prev).set(userId, data.items));
      } catch (err) {
        console.error('[Transcripts] Failed to load student courses:', err);
      } finally {
        setExpandedLoading((prev) => {
          const next = new Set(prev);
          next.delete(userId);
          return next;
        });
      }
    }
  };

  // Checkbox handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSet = new Set(selectedIds);
      filteredStudents.forEach((s) => newSet.add(s.user_id));
      setSelectedIds(newSet);
    } else {
      const newSet = new Set(selectedIds);
      filteredStudents.forEach((s) => newSet.delete(s.user_id));
      setSelectedIds(newSet);
    }
  };

  const handleSelectRow = (userId: number) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(userId)) {
      newSet.delete(userId);
    } else {
      newSet.add(userId);
    }
    setSelectedIds(newSet);
  };

  const allPageSelected =
    filteredStudents.length > 0 && filteredStudents.every((s) => selectedIds.has(s.user_id));
  const somePageSelected = filteredStudents.some((s) => selectedIds.has(s.user_id));

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '--';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Student PDF download — download via latest_transcript_id, mark ALL transcript_ids as downloaded
  const handleDownloadPdf = async (student: StudentTranscriptRow) => {
    try {
      const blob = await api.downloadTranscriptPdf(PARTNER_ID, student.latest_transcript_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${student.student_last_name}_${student.student_first_name}_${student.latest_transcript_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // Internal users viewing a partner must not flip the partner's review
      // state (SOPHIA-35095), so their downloads are never recorded
      if (!isInternal) {
        markDownloaded(student.transcript_ids);
        backendApi
          .recordTranscriptDownload(PARTNER_ID_STR, student.transcript_ids, 'single')
          .catch((err) => {
            console.error('[Transcripts] Failed to record download:', err);
          });
      }
    } catch (err) {
      console.error('[Transcripts] PDF download failed:', err);
      setError('Failed to download PDF');
    }
  };

  // Bulk PDF download — use latest_transcript_id from each selected student
  const handleBulkDownload = async () => {
    try {
      setBulkDownloading(true);
      setError(null);
      const selectedStudents = filteredStudents.filter((s) => selectedIds.has(s.user_id));
      const latestIds = selectedStudents.map((s) => s.latest_transcript_id);
      const allIds = selectedStudents.flatMap((s) => s.transcript_ids);
      const blob = await api.bulkDownloadTranscripts(PARTNER_ID, latestIds);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transcripts_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      if (!isInternal) {
        markDownloaded(allIds);
        backendApi.recordTranscriptDownload(PARTNER_ID_STR, allIds, 'bulk').catch((err) => {
          console.error('[Transcripts] Failed to record bulk download:', err);
        });
      }
      setSelectedIds(new Set());
    } catch (err) {
      console.error('[Transcripts] Bulk download failed:', err);
      setError('Failed to download transcripts');
    } finally {
      setBulkDownloading(false);
    }
  };

  // CSV export — one row per student, mirroring the table (SOPHIA-35098).
  // The server applies the same review-status filter (SOPHIA-35146), so the
  // export pages through exactly the rows the table would show; the Review
  // Status column is labelled from each row's status (server-provided when
  // filtering, else derived from download records for every exported id).
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      setError(null);

      const exportParams: TranscriptQueryParams = {
        sort: sortColumn,
        order: sortOrder,
        date_from: dateFromStr,
      };
      if (search) exportParams.search = search;
      if (dateToStr) exportParams.date_to = dateToStr;
      if (courseIdsStr) exportParams.course_ids = courseIdsStr;
      if (reviewFilterActive) {
        exportParams.review_status = reviewFilter.join(',');
        if (portalLaunchDate) exportParams.portal_launch_date = portalLaunchDate;
      }
      const rows = await fetchAllTranscriptStudents(api, PARTNER_ID, exportParams);

      // Label from the server's review_status when present; otherwise derive it,
      // which needs download info for every exported id, not just a page's.
      const needsDerivedStatus = rows.some((r) => r.review_status === undefined);
      let derived: (row: StudentTranscriptRow) => ReviewStatus = () => 'needs_review';
      if (needsDerivedStatus) {
        const allIds = rows.flatMap((r) => r.transcript_ids);
        const { my, org } = await fetchDownloadStatusForIds(backendApi, PARTNER_ID_STR, allIds);
        derived = (row) => getReviewStatus(row, my, org, portalLaunchDate);
      }
      const statusOf = (row: StudentTranscriptRow): ReviewStatus =>
        row.review_status ?? derived(row);

      const csv = transcriptStudentsToCSV(
        rows.map((row) => ({
          row,
          reviewStatus: reviewStatusConfig[statusOf(row)].label,
        }))
      );
      downloadCSV(csv, `sophia-transcripts-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      console.error('[Transcripts] CSV export failed:', err);
      setError(
        `Failed to export CSV${err instanceof Error ? ` — ${err.message}` : ''}. Try again; if it keeps failing, contact support.`
      );
    } finally {
      setExporting(false);
    }
  };

  const columnCount = isSmall ? 6 : 8;
  const exportEnabled = !exporting && !loading && displayTotal > 0;

  if (partnerId == null) {
    return <SelectPartnerPrompt />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {/* Page header + filters */}
      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="xl" sx={{ pt: 4, pb: 3 }}>
          <Typography variant="h1" gutterBottom>
            Transcripts
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {hasActiveFilters
              ? `${institutionName ? `${institutionName} — ` : ''}${
                  loading
                    ? 'Loading matching students…'
                    : `${displayTotal.toLocaleString()} matching students`
                }`
              : institutionName}
          </Typography>

          {/* Filter toolbar */}
          <Stack
            direction={isSmall ? 'column' : 'row'}
            spacing={1.5}
            alignItems={isSmall ? 'stretch' : 'center'}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search by student name or email..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              sx={{ minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <i
                      className="fa-thin fa-magnifying-glass"
                      style={{ color: 'rgba(0,0,0,0.54)', fontSize: 18 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchInput ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <i className="fa-thin fa-xmark" style={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={loading}
              sx={{ minWidth: 'auto', height: 40 }}
            >
              Search
            </Button>

            <DatePicker
              label="From"
              value={dateFrom}
              onChange={(val) => {
                setDateFrom(val);
                setPage(0);
                setSelectedIds(new Set());
              }}
              slots={{
                openPickerIcon: () => (
                  <i className="fa-thin fa-calendar" style={{ fontSize: 18 }} />
                ),
              }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />
            <DatePicker
              label="To"
              value={dateTo}
              minDate={dateFrom ?? undefined}
              onChange={(val) => {
                setDateTo(val);
                setPage(0);
                setSelectedIds(new Set());
              }}
              slots={{
                openPickerIcon: () => (
                  <i className="fa-thin fa-calendar" style={{ fontSize: 18 }} />
                ),
              }}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
            />

            <Autocomplete
              multiple
              size="small"
              options={availableCourses}
              getOptionLabel={(opt) => opt.title}
              value={selectedCourses}
              onChange={(_e, val) => {
                setSelectedCourses(val);
                setPage(0);
                setSelectedIds(new Set());
              }}
              renderInput={(params) => <TextField {...params} placeholder="Filter by course..." />}
              sx={{ minWidth: 260 }}
              limitTags={1}
            />

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel shrink>Review Status</InputLabel>
              <Select
                multiple
                value={reviewFilter}
                label="Review Status"
                notched
                onChange={(e) => {
                  const val = e.target.value as ReviewStatus[];
                  setReviewFilter(val);
                  setPage(0);
                  setSelectedIds(new Set());
                }}
                renderValue={(selected) =>
                  selected.length === 0
                    ? 'All'
                    : selected.map((s) => reviewStatusConfig[s].label).join(', ')
                }
                displayEmpty
              >
                {ALL_REVIEW_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox size="small" checked={reviewFilter.includes(status)} />
                    <Typography variant="body2" sx={{ ml: 0.5 }}>
                      {reviewStatusConfig[status].label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }} />

            <Tooltip
              title={
                exporting
                  ? `Exporting...`
                  : 'Export all matching students as CSV (one row per student)'
              }
            >
              <Typography
                component="span"
                variant="body2"
                onClick={exportEnabled ? handleExportCSV : undefined}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  color: exportEnabled ? 'primary.main' : 'text.disabled',
                  fontWeight: 600,
                  cursor: exportEnabled ? 'pointer' : 'default',
                  whiteSpace: 'nowrap',
                  '&:hover': exportEnabled ? { textDecoration: 'underline' } : {},
                }}
              >
                {exporting ? (
                  <CircularProgress size={16} />
                ) : (
                  <i className="fa-thin fa-file-arrow-down" style={{ fontSize: 18 }} />
                )}
                Export CSV
              </Typography>
            </Tooltip>
          </Stack>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              {search && (
                <Chip label={`Search: "${search}"`} onDelete={handleClearSearch} size="small" />
              )}
              {dateFrom && (
                <Chip
                  label={`From: ${dateFrom.format('MMM D, YYYY')}`}
                  onDelete={handleClearDateFrom}
                  size="small"
                />
              )}
              {dateTo && (
                <Chip
                  label={`To: ${dateTo.format('MMM D, YYYY')}`}
                  onDelete={handleClearDateTo}
                  size="small"
                />
              )}
              {selectedCourses.map((c) => (
                <Chip
                  key={c.id}
                  label={c.title}
                  onDelete={() => {
                    setSelectedCourses(selectedCourses.filter((sc) => sc.id !== c.id));
                    setPage(0);
                    setSelectedIds(new Set());
                  }}
                  size="small"
                />
              ))}
              {(reviewFilter.length !== DEFAULT_REVIEW_FILTER.length ||
                !DEFAULT_REVIEW_FILTER.every((s) => reviewFilter.includes(s))) && (
                <Chip
                  label={`Status: ${reviewFilter.length === 0 || reviewFilter.length === ALL_REVIEW_STATUSES.length ? 'All' : reviewFilter.map((s) => reviewStatusConfig[s].label).join(', ')}`}
                  onDelete={() => {
                    setReviewFilter([...DEFAULT_REVIEW_FILTER]);
                    setPage(0);
                    setSelectedIds(new Set());
                  }}
                  size="small"
                />
              )}
              <Chip
                label="Clear all"
                onClick={handleClearAllFilters}
                size="small"
                variant="outlined"
              />
            </Stack>
          )}
        </Container>
      </Box>

      {/* Content area */}
      <Box sx={{ bgcolor: 'background.paper', flex: 1 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          {/* Bulk selection bar */}
          {selectedIds.size > 0 && (
            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleBulkDownload}
                disabled={bulkDownloading}
                startIcon={
                  bulkDownloading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <i className="fa-thin fa-file-arrow-down" style={{ fontSize: 18 }} />
                  )
                }
              >
                {bulkDownloading ? 'Downloading...' : `Download ${selectedIds.size} selected`}
              </Button>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="none" sx={{ width: 36, pl: 1 }} />
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={somePageSelected && !allPageSelected}
                        checked={allPageSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'student_name'}
                        direction={sortColumn === 'student_name' ? sortOrder : 'asc'}
                        onClick={() => handleSort('student_name')}
                      >
                        Student
                      </TableSortLabel>
                    </TableCell>
                    {!isSmall && <TableCell>Email</TableCell>}
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortColumn === 'course_count'}
                        direction={sortColumn === 'course_count' ? sortOrder : 'asc'}
                        onClick={() => handleSort('course_count')}
                      >
                        Courses
                      </TableSortLabel>
                    </TableCell>
                    {!isSmall && <TableCell>Status</TableCell>}
                    <TableCell>
                      <TableSortLabel
                        active={sortColumn === 'latest_certification_date'}
                        direction={sortColumn === 'latest_certification_date' ? sortOrder : 'asc'}
                        onClick={() => handleSort('latest_certification_date')}
                      >
                        Latest Certified
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">PDF</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                      </TableCell>
                    </TableRow>
                  ) : filteredStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={columnCount} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">
                          {hasActiveFilters || students.length > 0
                            ? 'No students match your filters.'
                            : 'No transcripts found.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((student) => {
                      const isExpanded = expandedStudentId === student.user_id;
                      const isLoading = expandedLoading.has(student.user_id);
                      const courses = expandedCourses.get(student.user_id) ?? [];
                      const status = statusOfRow(student);
                      const preLaunch = isPreLaunchReviewed(
                        student,
                        myDownloads,
                        orgDownloads,
                        portalLaunchDate
                      );
                      const myDownload = myDownloads[student.latest_transcript_id];
                      const myReviewed = student.transcript_ids.length > 0 && !!myDownload;
                      const orgDownload = student.transcript_ids
                        .map((id) => orgDownloads[id])
                        .find(Boolean);

                      return (
                        <React.Fragment key={student.user_id}>
                          {/* Student summary row */}
                          <TableRow
                            hover
                            selected={selectedIds.has(student.user_id)}
                            sx={{ cursor: 'pointer' }}
                          >
                            {/* Expand chevron */}
                            <TableCell padding="none" sx={{ width: 36, pl: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleToggleExpand(student.user_id)}
                              >
                                <i
                                  className={`fa-thin ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                                  style={{ fontSize: 14 }}
                                />
                              </IconButton>
                            </TableCell>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedIds.has(student.user_id)}
                                size="small"
                                onChange={() => handleSelectRow(student.user_id)}
                              />
                            </TableCell>
                            <TableCell onClick={() => handleToggleExpand(student.user_id)}>
                              {student.student_last_name}, {student.student_first_name}
                              {status === 'needs_review' && (
                                <Tooltip title="New to your Transcripts list — no one on your team has downloaded this student's transcript yet.">
                                  <Chip
                                    label="NEW"
                                    color="info"
                                    size="small"
                                    sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                  />
                                </Tooltip>
                              )}
                            </TableCell>
                            {!isSmall && (
                              <TableCell onClick={() => handleToggleExpand(student.user_id)}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ maxWidth: 200 }}
                                >
                                  {student.student_email}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell
                              align="right"
                              onClick={() => handleToggleExpand(student.user_id)}
                            >
                              {student.course_count}
                            </TableCell>
                            {!isSmall && (
                              <TableCell onClick={() => handleToggleExpand(student.user_id)}>
                                <ReviewStatusChip status={status} preLaunch={preLaunch} />
                              </TableCell>
                            )}
                            <TableCell onClick={() => handleToggleExpand(student.user_id)}>
                              {formatDate(student.latest_certification_date)}
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                <Tooltip
                                  title={
                                    myReviewed && myDownload
                                      ? `Downloaded${myDownload.download_origin === 'api' ? ' via API' : ''} by ${user?.email ?? 'you'} on ${formatDate(myDownload.downloaded_at)}`
                                      : status === 'updated'
                                        ? 'Modified — new courses since last download'
                                        : 'Download transcript PDF'
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDownloadPdf(student);
                                    }}
                                    sx={{ width: 32, height: 32, borderRadius: '50%' }}
                                  >
                                    <i
                                      className={
                                        status === 'reviewed'
                                          ? 'fa-solid fa-file-arrow-down'
                                          : 'fa-thin fa-file-arrow-down'
                                      }
                                      style={{ fontSize: 18 }}
                                    />
                                  </IconButton>
                                </Tooltip>
                                {myReviewed && (
                                  <Tooltip
                                    title={`Downloaded${myDownload?.download_origin === 'api' ? ' via API' : ''} by ${user?.email ?? 'you'} on ${formatDate(myDownload!.downloaded_at)}`}
                                  >
                                    <i
                                      className="fa-thin fa-circle-check"
                                      style={{ fontSize: 14, color: '#4caf50' }}
                                    />
                                  </Tooltip>
                                )}
                                {!myReviewed && orgDownload && (
                                  <Tooltip
                                    title={`Downloaded${orgDownload.download_origin === 'api' ? ' via API' : ''} by ${orgDownload.user_email} on ${formatDate(orgDownload.downloaded_at)}`}
                                  >
                                    <i
                                      className="fa-thin fa-circle-check"
                                      style={{ fontSize: 14, color: '#4caf50' }}
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>

                          {/* Expanded course rows */}
                          <TableRow>
                            <TableCell colSpan={columnCount} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} unmountOnExit>
                                <Box sx={{ py: 1, px: 4, bgcolor: 'action.hover' }}>
                                  {isLoading ? (
                                    <Box sx={{ py: 2, textAlign: 'center' }}>
                                      <CircularProgress size={20} />
                                    </Box>
                                  ) : (
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell sx={{ fontWeight: 600 }}>Course</TableCell>
                                          {!isSmall && (
                                            <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                                          )}
                                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                                            Credits
                                          </TableCell>
                                          <TableCell sx={{ fontWeight: 600 }}>Certified</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {courses.map((course) => (
                                          <TableRow key={course.id}>
                                            <TableCell>
                                              {course.course_title}
                                              {isCourseNew(
                                                status,
                                                course.id,
                                                course.certification_date,
                                                myDownloads,
                                                orgDownloads,
                                                portalLaunchDate
                                              ) && (
                                                <Tooltip title="Not downloaded by your team yet.">
                                                  <Chip
                                                    label="NEW"
                                                    color="info"
                                                    size="small"
                                                    sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                                                  />
                                                </Tooltip>
                                              )}
                                            </TableCell>
                                            {!isSmall && (
                                              <TableCell>
                                                <Typography variant="body2" color="text.secondary">
                                                  {course.course_code}
                                                </Typography>
                                              </TableCell>
                                            )}
                                            <TableCell align="right">
                                              {course.credits == null
                                                ? '—'
                                                : parseFloat(course.credits).toFixed(1)}
                                            </TableCell>
                                            <TableCell>
                                              {formatDate(course.certification_date)}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={displayTotal}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count} students`}
            />
          </Paper>
        </Container>
      </Box>
    </LocalizationProvider>
  );
}
